import React, { memo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { Users, Sparkles, Target, TrendingUp } from 'lucide-react';
import { useGroups } from '@/hooks/queries/useGroups';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * ✅ FASE 3+9: Componente condicional de grupos na Home com memoization
 * Mostra CTA para descobrir grupos OU status dos grupos do usuário
 */
export const GroupsHomeSection = memo(() => {
  const { myGroups, isLoading } = useGroups();
  const navigate = useNavigate();

  const handleDiscoverClick = useCallback(() => {
    navigate('/grupos/descobrir');
  }, [navigate]);

  const handleViewAllClick = useCallback(() => {
    navigate('/grupos');
  }, [navigate]);

  const handleGroupClick = useCallback((groupId: string) => {
    navigate(`/grupo/${groupId}`);
  }, [navigate]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  // ✅ CASO 1: Sem grupos - CTA para descobrir
  if (!myGroups || myGroups.length === 0) {
    return (
      <Card 
        className="border-2 border-dashed hover:border-primary transition-all cursor-pointer bg-gradient-to-br from-primary/5 to-primary/10"
        onClick={handleDiscoverClick}
      >
        <CardContent className="pt-8 pb-8 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 mx-auto mb-4">
            <Users className="w-8 h-8 text-primary" />
          </div>
          <h3 className="font-semibold text-xl mb-2">
            Junte-se a um grupo!
          </h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
            Conecte-se com pessoas que compartilham seus objetivos.
            Juntos, vocês vão mais longe! 🚀
          </p>
          <Button size="lg" className="gap-2">
            <Sparkles className="w-4 h-4" />
            Descobrir Grupos
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ✅ CASO 2: Com grupos - Mostrar status e cards compactos
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Meus Grupos ({myGroups.length})
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleViewAllClick}
          >
            Ver todos
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {myGroups.slice(0, 3).map(group => {
          // Calcular progresso da missão atual se existir
          const currentMission = group.current_mission;
          const missionProgress = currentMission 
            ? Math.round((currentMission.progresso_atual / currentMission.progresso_total) * 100)
            : null;

          return (
            <div 
              key={group.id}
              className="group p-4 rounded-lg border hover:border-primary hover:shadow-md transition-all cursor-pointer bg-card"
              onClick={() => handleGroupClick(group.id)}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <p className="font-medium group-hover:text-primary transition-colors">
                    {group.nome}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-xs text-muted-foreground">
                      {group.member_count} {group.member_count === 1 ? 'membro' : 'membros'}
                    </p>
                    {group.nivel_grupo > 1 && (
                      <>
                        <span className="text-xs text-muted-foreground">•</span>
                        <Badge variant="secondary" className="h-5 px-2 text-xs">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          Nível {group.nivel_grupo}
                        </Badge>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Missão ativa */}
              {currentMission && (
                <div className="mt-3 pt-3 border-t">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-primary" />
                      <p className="text-xs font-medium">
                        {currentMission.titulo}
                      </p>
                    </div>
                    <Badge 
                      variant={missionProgress === 100 ? "default" : "secondary"}
                      className="h-5 px-2 text-xs"
                    >
                      {missionProgress}%
                    </Badge>
                  </div>
                </div>
              )}

              {/* Sem missão */}
              {!currentMission && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Target className="w-3 h-3" />
                    Nenhuma missão ativa
                  </p>
                </div>
              )}
            </div>
          );
        })}

        {myGroups.length > 3 && (
          <Button 
            variant="outline" 
            className="w-full mt-2"
            onClick={handleViewAllClick}
          >
            Ver mais {myGroups.length - 3} {myGroups.length - 3 === 1 ? 'grupo' : 'grupos'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
});

GroupsHomeSection.displayName = 'GroupsHomeSection';
